import React, { useState, useEffect, useContext } from 'react';
import { Grid, Box } from '@material-ui/core';
import { AttributeAsUsernameComponent, AttributeAsCustomComponent } from './attributeTransform';
import { availableAttributes, ATTRIBUTE_ISSUERS, NAME_IDENTIFIER_ATTRIBUTE } from '@app/pages/ssoManager/addEditApplication.enums';
import { useTypedTranslation } from '@app/hooks';
import AppsContext from './appContext';
import { issueAsUsername, issueAsCustom } from '@app/helpers/addEditApplication.helpers';
import { ICustomAttribute } from '@app/models';
import { RadioButton } from '@itglue/kaseya-ui';

export const AttributeTransform: React.SFC = () => {
    const { translation } = useTypedTranslation();

    const app = useContext(AppsContext);
    const [state, setstate] = useState<{
        attributeIssued: string;
        usernameIssuedAttr: string;
        newAttributeType: string;
        customAttributeMaps: ICustomAttribute[];
    }>({
        attributeIssued: ATTRIBUTE_ISSUERS.username,
        usernameIssuedAttr: availableAttributes[0],
        newAttributeType: '',
        customAttributeMaps: []
    });
    const [usernameAttributeIssuersSelected, setUsernameAttributeIssuersSelected] = useState<boolean>(false);

    useEffect(() => {
        const attrMapConfig = app.AttributeRules ? JSON.parse(app.AttributeRules as string) : {};

        let customSelection = true;

        const nameIdentifierType = NAME_IDENTIFIER_ATTRIBUTE;

        if (attrMapConfig?.Entries?.length === 0) {
            customSelection = false;
            setstate((prevState) => ({ ...prevState, usernameIssuedAttr: availableAttributes[0], newAttributeType: nameIdentifierType }));
        } else if (attrMapConfig?.Entries?.length === 1) {
            const rule = attrMapConfig.Entries[0];

            let ruleConfig = rule.Properties ?? null;

            if (rule.Type === nameIdentifierType && rule.Emit === '{User.PrincipalName}' && ruleConfig && Object.keys(ruleConfig).length) {
                customSelection = false;
                setstate((prevState) => ({ ...prevState, usernameIssuedAttr: availableAttributes[0], newAttributeType: rule.Type }));
            }
        }

        if ((state.attributeIssued === ATTRIBUTE_ISSUERS.custom || customSelection) && !usernameAttributeIssuersSelected)
            setstate((prevState) => ({
                ...prevState,
                customAttributeMaps: attrMapConfig?.Entries ? [...attrMapConfig?.Entries] : [],
                attributeIssued: ATTRIBUTE_ISSUERS.custom
            }));
    }, [app.AttributeRules]);

    const generateAttributeMapConfig = (attributeMaps: ICustomAttribute[]) => {
        let map: ICustomAttribute[] = [];
        if (issueAsUsername(state.attributeIssued)) {
            map.push({ Emit: attributeMaps[0].Emit, Type: NAME_IDENTIFIER_ATTRIBUTE, Properties: {} });
        } else if (attributeMaps) {
            attributeMaps.forEach(function (m) {
                map.push(m);
            });
        }

        const mapping = { Entries: map };

        return JSON.stringify(mapping);
    };

    const handleIssuerTypeChangeChange = (value: string) => {
        setstate({ ...state, attributeIssued: value });
        if (value === ATTRIBUTE_ISSUERS.username) {
            let newAttributeMaps: ICustomAttribute[] = [
                { Emit: availableAttributes[0], Type: NAME_IDENTIFIER_ATTRIBUTE, Properties: {} } as ICustomAttribute
            ];
            app.updateState('AttributeRules', generateAttributeMapConfig(newAttributeMaps));
            setUsernameAttributeIssuersSelected(true);
        }
    };

    const handleChange = (name: string, value: any) => {
        setstate((prevState) => ({ ...prevState, [name]: value }));
        let newAttributeMaps: ICustomAttribute[] = [{ Emit: value } as ICustomAttribute];
        app.updateState('AttributeRules', generateAttributeMapConfig(newAttributeMaps));
    };

    const handleOnGetCustomAttribute = (index: number) => {
        const currentAttributeList = [...state.customAttributeMaps];

        return currentAttributeList[index];
    };

    const handleOnDeleteCustomAttribute = (index: number) => {
        const currentAttributeList = [...state.customAttributeMaps];
        currentAttributeList.splice(index, 1);
        app.updateState('AttributeRules', generateAttributeMapConfig(currentAttributeList));
    };

    const handleOnAddCustomAttribute = (index: number, customAttribute: { Emit: string; Type: string; Properties: {} }) => {
        let newAttributeMaps: ICustomAttribute[];

        if (index >= 0) {
            newAttributeMaps = [...state.customAttributeMaps];
            newAttributeMaps[index] = customAttribute;
        } else {
            newAttributeMaps = [...state.customAttributeMaps, customAttribute];
        }

        app.updateState('AttributeRules', generateAttributeMapConfig(newAttributeMaps));
    };

    return (
        <>
            <Grid container justifyContent='space-between' className='configpanel-row'>
                <Grid item xs={12} lg={8} className='configpanel-left'>
                    <Box p={2}>
                        <RadioButton
                            value={ATTRIBUTE_ISSUERS.username}
                            label={translation('ATTR_ISSUE_AS_USERNAME')}
                            name='attribute'
                            checked={state.attributeIssued === ATTRIBUTE_ISSUERS.username}
                            onChange={handleIssuerTypeChangeChange}
                        />

                        {issueAsUsername(state.attributeIssued) && (
                            <div className='configpanel__username-attr'>
                                <AttributeAsUsernameComponent
                                    value={state.usernameIssuedAttr}
                                    onChange={({ target: { name, value } }) => handleChange(name as string, value)}
                                />
                            </div>
                        )}
                        <RadioButton
                            value={ATTRIBUTE_ISSUERS.custom}
                            label={translation('ATTR_ISSUE_CUSTOM')}
                            name='attribute'
                            checked={state.attributeIssued === ATTRIBUTE_ISSUERS.custom}
                            onChange={handleIssuerTypeChangeChange}
                        />
                        {issueAsCustom(state.attributeIssued) && (
                            <AttributeAsCustomComponent
                                customAttributeMaps={state.customAttributeMaps}
                                onGet={handleOnGetCustomAttribute}
                                onDelete={handleOnDeleteCustomAttribute}
                                onAdd={handleOnAddCustomAttribute}
                            />
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} lg={4} className='configpanel-right'>
                    <Box p={4}>
                        <div dangerouslySetInnerHTML={{ __html: translation('ATTR_SELECTOR_HELP') }}></div>
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};
